import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, FlagIcon, FolderSearchIcon, SnowflakeIcon, TimerIcon, UsersIcon, BabyIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Mockup standard «GOLFKLAR™ · 6 bind» (Higgsfield gpt_image_2_5, see golfklar/media/MOCKUP.md).
export const PRODUCT_IMAGE = 'https://d2ol7oe51mr4n9.cloudfront.net/user_37MCUZnyYhEzEhcHxirwYMezT12/3984297c-ea37-4b71-bb45-c82856e1aafb.webp';
export const BRAND = 'GOLFKLAR™';
export const DISCLAIMER = 'Ikke en offisiell utgivelse fra Norges Golfforbund. Ved skade eller smerter: rådfør deg med helsepersonell.';

export const heroGuarantees = ['Direkte nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dager pengene-tilbake-garanti'];

export const stats = [{
  value: '6',
  label: 'Digitale bind'
}, {
  value: '240',
  label: 'Sider klare til bruk'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: 'PDF',
  label: 'Klar til utskrift'
}, {
  value: '3',
  label: 'Nivåer i hver økt'
}];

export const differenceStats = [{
  value: 'Nybegynner → klubbspiller',
  label: 'Fra første slag på rangen til en tydelig plan for utvikling'
}, {
  value: '6 digitale bind',
  label: 'Fra sesongplan og gruppeøkter til banestrategi, tester og oppfølging'
}, {
  value: 'Ute og inne',
  label: 'Range, øvingsgreen og bane om sommeren – simulator og innendørs putting om vinteren'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Søndagskvelden går til planlegging',
  text: 'Du sitter foran PC-en søndag kveld og planlegger ukas gruppetreninger – og blir likevel ikke ferdig.'
}, {
  title: 'Blandede nivåer i samme gruppe',
  text: 'Nybegynneren som så vidt treffer ballen og klubbspilleren med lavt handicap står på samme range – og økta må fungere for begge.'
}, {
  title: 'Juniorer som kjeder seg på rangen',
  text: 'Etter ti minutter med baller fra matta har halve juniorgruppen mistet konsentrasjonen.'
}, {
  title: 'Vinteren uten plan',
  text: 'Fra oktober til april flytter treningen inn i simulatoren og på innendørs putting – men uten en plan blir det bare «slå noen baller».'
}, {
  title: 'De samme øktene om igjen',
  text: 'Uten en sesongplan blir det lett driver og jern hver gang, mens kortspill og putting blir glemt.'
}, {
  title: 'Leting etter øvelser på nettet',
  text: 'Videoene du finner er laget for én spiller – ikke for en gruppe på 6–20 med ulike nivåer.'
}];

export const afterItems = ['Et ferdig årshjul for golfgruppen – ute og inne', 'Ukeplaner for 1 og 2 økter i uka', '12 ferdige gruppeøkter minutt for minutt', 'Hver økt i 3 nivåer – hele gruppen trener sammen', 'Stasjonskart for range og øvingsgreen', 'Vinter- og simulatorøkter', 'Sving, kortspill og putting med drills per nivå', '8 ukers nybegynnerprogram mot grønt kort', 'Tester og poengtabeller ferdig utregnet'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Søndagskvelden går med til planlegging',
  text: 'Bruker du søndagskvelden på å planlegge ukas gruppetreninger fra bunnen av?'
}, {
  icon: UsersIcon,
  title: 'Gruppen har blandede nivåer',
  text: 'Trenger du økter der nybegynneren og den erfarne klubbspilleren kan trene sammen – på hvert sitt nivå?'
}, {
  icon: BabyIcon,
  title: 'Juniorene kjeder seg',
  text: 'Skulle du ønske at juniorgruppen fikk lek, spill og konkurranser i stedet for bare baller fra matta?'
}, {
  icon: SnowflakeIcon,
  title: 'Innesesongen kommer',
  text: 'Trenger du en plan for simulator, nett og innendørs putting fra oktober til april?'
}, {
  icon: TimerIcon,
  title: 'Klokka er 17.55 og ingenting er klart',
  text: 'Står du på rangen med spillerne på vei – uten en tydelig plan for økta?'
}, {
  icon: FolderSearchIcon,
  title: 'Du leter etter øvelser på nettet',
  text: 'Bruker du tid på å lete etter drills som passer gruppen, nivået og sesongen?'
}];

// Spine colours mirror the box: navy, sky blue, green, lime, orange, red.
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: 'Sesongplan',
  title: 'Sesongplan, periodisering og 12 ferdige gruppeøkter',
  tagline: 'Vit nøyaktig hva hver gruppetrening skal inneholde – hele sesongen.',
  intro: 'Det første bindet gir deg årshjulet for golfgruppen og tolv ferdige gruppeøkter du kan bruke direkte.',
  items: ['Årshjul for 52 uker', 'Innesesong og simulator nov–mars', 'Forsesong april–mai', 'Hovedsesong juni–august', 'Høst og overgang', 'Ukeplaner for 1 og 2 økter', '12 økter minutt for minutt (60 min)', '4 innendørs- og simulatorøkter', 'Stasjonskart range og øvingsgreen', 'Sjekkliste før økt'],
  outro: 'Ferdige økter som gjør at du møter gruppen med en tydelig plan.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '50 økter',
  title: 'Øktbanken: 50 ferdige gruppeøkter',
  tagline: 'Femti ferdige økter – velg, skriv ut, tren.',
  intro: 'Det andre bindet samler øktene du trenger gjennom året, hver med mål, varighet, stasjoner og tre nivåer.',
  items: ['Range: jern, driver og wedge', 'Kortspill', 'Putting', 'Bane: 9 hull-trening', 'Innendørs og simulator', 'Regnværsøkter', 'Mål, varighet og stasjoner på hver økt'],
  outro: 'Variasjon som holder gruppen motivert – uten at du må finne på alt selv.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '40 drills',
  title: 'Sving og teknikk: 40 drills per nivå',
  tagline: 'Bedre grunnleggende teknikk – og en plan for å rette feil.',
  intro: 'Det tredje bindet gir deg drills for grep, oppstilling og sving, med læringspunkter for hvert nivå.',
  items: ['Grep, oppstilling og ballposisjon', 'Svingplan og tempo', 'Ballkontakt og treff', 'Driver, jern og wedge', 'Vanlige feil: slice, topp, feit treff', 'Korreksjon steg for steg', 'Observasjonsskjema for treneren'],
  outro: 'Teknikkarbeid som passer inn i hver gruppetrening.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '30 spill',
  title: 'Kortspill og putting',
  tagline: 'Der slagene spares – trent på en måte gruppen elsker.',
  intro: 'Det fjerde bindet gir deg kortspill og putting med konkurranser og spill for øvingsgreen.',
  items: ['Chip og pitch', 'Bunkerslag', 'Lagputting og korte putter', 'Avstandskontroll', '30 konkurranser og spill for øvingsgreen', 'Testløyper'],
  outro: 'Kortspill og putting som blir høydepunktet i økta.',
  spine: 'bg-lime-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '3 nivåer',
  title: 'Junior og nybegynnere: grupper på ulike nivåer',
  tagline: 'Én økt, tre nivåer – og en vei videre for alle.',
  intro: 'Det femte bindet viser hvordan du deler gruppen i nivåer og tar nybegynnere og juniorer trygt videre.',
  items: ['Nivåinndeling', '«Én økt, tre nivåer»', '8 ukers nybegynnerprogram mot grønt kort', 'Regler, etikette og sikkerhet', 'Første runde på banen', 'Juniorgrupper 8–12 og 13–17 med lek og spill', 'Voksne nybegynnere', 'Samarbeid med foreldre'],
  outro: 'Alle i gruppen får en plan – fra første slag til klubbspiller.',
  note: `Nybegynnerprogrammet er en forberedelse mot grønt kort. ${BRAND} er ikke et offisielt «Veien til golf»-kurs.`,
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: 'Tester',
  title: 'Banestrategi, mental trening, testing og oppfølging',
  tagline: 'Spill smartere – og følg utviklingen.',
  intro: 'Det sjette bindet gir deg banestrategi, mental trening, tester med poengtabeller og verktøy for å følge opp hver spiller.',
  items: ['Banestrategi hull for hull', 'Pre-shot-rutine', 'Mental trening: fokus, nerver og reset', 'Tester: putting 1–3 m, lagputt, chip, pitch og range', 'Poengtabeller', 'Spillerlogg og progresjonsskjema', 'Evalueringssamtale', '100 ferdige tilbakemeldinger til spillere'],
  outro: 'Komplett oppfølging – uten regneark og kveldsarbeid.',
  note: `${BRAND} er et selvstendig utviklet materiell. ${DISCLAIMER}`,
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitale bind for golftrenere',
  text: 'Et komplett system som hjelper deg å planlegge og lede gruppetreningene gjennom hele golfsesongen – ute og inne. Hvert bind har 40 sider med ferdige økter, drills og tester klare til utskrift.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige økter minutt for minutt',
  text: 'Oppvarming · hovedøkt med stasjoner · spill og konkurranse · oppsummering. Med utstyr og trenerens instruksjon.'
}, {
  icon: FileTextIcon,
  title: 'Klar til utskrift',
  text: 'Hele økta på papir eller på mobilen – ta den med ut på rangen eller inn i simulatoren.'
}, {
  icon: UsersIcon,
  title: 'Tre nivåer i hver økt',
  text: 'Samme økt, tre varianter. Nybegynnere og erfarne spillere trener sammen – ingen står og venter.'
}, {
  icon: FlagIcon,
  title: 'Range, øvingsgreen og bane',
  text: 'Jern, driver, wedge, chip, pitch, bunker, putting og 9 hull-trening med stasjonskart.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Tester og poengtabeller',
  text: 'Putting 1–3 m, lagputt, chip-, pitch- og range-test med poengtabeller og progresjonsskjema.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årshjul for golfgruppen',
  text: 'Innesesong og simulator, forsesong, hovedsesong, høst og overgang – uke for uke.'
}];

export { pageSamples } from './pageSamples';
export const pageTypes = ['Årshjul', 'Ferdige gruppeøkter', 'Økter i 3 nivåer', 'Stasjonskart'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Vikartrener-pakken',
  text: 'Ferdige økter hjelpetreneren kan lede når du ikke selv kan være der.',
  items: ['12 ferdige økter', '6 ute og 6 inne', 'Tydelige instrukser', 'Klart til utskrift'],
  benefit: 'Syk eller bortreist? Treningen går likevel.'
}, {
  title: 'Oppvarming og drills på kort',
  text: 'Printbare kort med oppvarming, sving, kortspill og putting – rett i bagen.',
  items: ['60 printbare kort', 'Oppvarming', 'Sving og kortspill', 'Putting'],
  benefit: 'En ny øvelse hver uke – uten forberedelse.'
}, {
  title: 'Tester og poengkort',
  text: 'Ferdige testprotokoller med poengtabeller og nivåmerker.',
  items: ['Putting', 'Chip og pitch', 'Range', 'Nivåmerker'],
  benefit: 'Se fremgangen svart på hvitt – for hver spiller.'
}, {
  title: 'Klubbturnering og juniordag trinn for trinn',
  text: 'Alt du trenger når klubben skal arrangere turnering eller juniordag.',
  items: ['Sjekklister', 'Slagspill, Texas scramble, poengbogey og match', 'Startlister', 'Premieutdeling'],
  benefit: 'En vellykket turneringsdag – uten kaos.'
}, {
  title: 'Kommunikasjon med gruppen og foreldrene',
  text: 'Ferdige meldinger til Spond, e-post eller Facebook-gruppen.',
  items: ['40 ferdige meldinger', 'Velkomst og sesongstart', 'Avlyst pga. vær', 'Innesesong og påmelding'],
  benefit: 'Meldingen til gruppen skrevet på et minutt.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: `Kjøp ${BRAND}`,
  text: 'Gjennomfør kjøpet på et par minutter. Engangsbetaling, ingen abonnement.'
}, {
  title: 'Last ned PDF-ene på e-post',
  text: 'Du får PDF-ene tilsendt på e-post med en gang etter kjøpet.'
}, {
  title: 'Velg økta og møt gruppen',
  text: 'Finn uka i årshjulet, skriv ut økta – og møt spillerne med en tydelig plan.'
}];

export const audienceItems = ['Er trener eller juniortrener i en golfklubb', 'Leder gruppetreninger for juniorer, voksne, damer eller seniorer', 'Har spillere på ulike nivåer i samme gruppe', 'Er frivillig trener og vil ha ferdige opplegg', 'Vil slippe å planlegge hver økt fra bunnen av', 'Leder nybegynnergrupper mot grønt kort', 'Vil ha en god plan for simulator og innesesong', 'Vil ha søndagskvelden tilbake'];

export const timeValueTasks = ['Lete etter øvelser på nettet', 'Planlegge ukas gruppetreninger', 'Dele gruppen i nivåer', 'Lage tester og poengkort', 'Skrive meldinger til gruppen og foreldrene'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Sesongplan og gruppeøkter, øktbank, sving og teknikk, kortspill og putting, junior og nybegynnere, banestrategi og tester'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Vikartrener-pakken, drillkort, tester og poengkort, klubbturnering og meldinger til gruppen'
}, {
  label: 'For golftrenere',
  detail: 'Ferdige økter i 3 nivåer, stasjonskart, vinter- og simulatorøkter, tester og oppfølging'
}];

export const offerIncludes = ['Direkte nedlasting', 'PDF-filer på e-post', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE = '299 kr';
export const PRICE_BEFORE = '599 kr';

// No testimonials on this page. Only authentic reviews may ever be added here.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hvilket format har materiellet?',
  a: `${BRAND} leveres digitalt som PDF-filer. Du kan bruke dem på PC, nettbrett eller mobil og skrive ut sidene du trenger.`
}, {
  q: 'Hvordan får jeg materiellet?',
  a: 'Du får PDF-ene på e-post med en gang etter kjøpet. Ingen ventetid og ingen fysisk vare.'
}, {
  q: 'Passer det for grupper med blandede nivåer?',
  a: 'Ja. Hver gruppeøkt har tre nivåer, så nybegynnere og erfarne spillere kan trene samtidig. Bind 5 viser hvordan du deler gruppen i nivåer.'
}, {
  q: 'Fungerer det om vinteren og i simulator?',
  a: 'Ja. Årshjulet har en egen innesesong, og du får økter for simulator, nett og innendørs putting – i tillegg til regnværsøkter i øktbanken.'
}, {
  q: 'Passer det for juniorer?',
  a: 'Ja. Bind 5 har juniorgrupper 8–12 og 13–17 med lek, spill og konkurranser, og Bind 4 har 30 spill for øvingsgreen.'
}, {
  q: 'Er dette et kurs for grønt kort?',
  a: `Nei. ${BRAND} er ikke et offisielt «Veien til golf»-kurs. Bind 5 har et 8 ukers nybegynnerprogram som forberedelse mot grønt kort, med regler, etikette og sikkerhet. Selve grønt kort tas i klubben din.`
}, {
  q: 'Kan jeg skrive det ut?',
  a: 'Ja. Alt er laget klart til utskrift. Du velger selv hva du skriver ut, og du kan også bruke materiellet digitalt på mobilen ute på rangen.'
}, {
  q: 'Trenger jeg trenerkurs for å bruke det?',
  a: 'Nei. Øktene er beskrevet minutt for minutt med trenerens instruksjon og utstyr. Materiellet passer både for frivillige trenere og for deg som har tatt trenerutdanning.'
}, {
  q: 'Er dette godkjent av NGF?',
  a: `Nei. ${BRAND} er et selvstendig utviklet materiell og er ikke godkjent av eller tilknyttet Norges Golfforbund (NGF). ${DISCLAIMER}`
}, {
  q: 'Kan jeg virkelig være klar til gruppetrening på 5 minutter?',
  a: `${BRAND} er laget for å gjøre forberedelsene raskere. Du finner uka i årshjulet, leser gjennom økta og er klar på omtrent fem minutter. Tiden kan variere etter økta, gruppen og behovet for tilpasning.`
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materiellet. Ingen faste trekk.'
}, {
  q: 'Finnes det en garanti?',
  a: `Ja. ${BRAND} har 7 dager pengene-tilbake-garanti. Hvis materiellet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen 7 dager etter kjøpet.`
}];

export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
